import {
  SITE_ROLES,
  type SimulatableRolesType,
  type SiteRolesType,
} from '@packages/domains/authorization/permissions/catalog';

export {
  ALLOWED_SITE_ROLES,
  BASE_ROLE_TO_SITE_ROLE,
  BaseSiteRolePermissions,
  BranchDesignerSitePermissions,
  BranchDesignerSiteRole,
  CLIENT_ALLOWED_SITE_ROLES,
  CommenterSitePermissions,
  CommenterSiteRole,
  DEFAULT_SITE_ROLES,
  DesignerSitePermissions,
  DesignerSiteRole,
  EditorSitePermissions,
  EditorSiteRole,
  getSitePermissionsByRole,
  isClientAllowedSiteRole,
  LEGACY_SITE_ROLE,
  LimitedDesignerSitePermissions,
  LimitedDesignerSiteRole,
  MarketerSitePermissions,
  MarketerSiteRole,
  ProjectAdminSitePermissions,
  ProjectAdminSiteRole,
  PublicSitePermissions,
  PublicSiteRole,
  SAVED_SITE_ROLES,
  SEAT_TIER_ORDER,
  SELECTABLE_SITE_ROLES,
  siteRoles,
  SITE_ROLE_KEYS,
  SITE_ROLE_OPTIONS,
  SITE_ROLES,
  SITE_ROLES_LIST,
  TIER_SITE_ROLES,
  toSiteRole,
  toSiteRoleType,
  ViewerSitePermissions,
  ViewerSiteRole,
  type ClientAllowedSiteRoles,
  type SimulatableRolesType,
  type SiteActivityLogResourcePermissions,
  type SiteAeoAnalyticsPermissions,
  type SiteAeoBrandPermissions,
  type SiteAeoLLMConfigPermissions,
  type SiteAeoRecommendationsPermissions,
  type SiteAIPermissions,
  type SiteAgentInstructionsPermissions,
  type SiteAgenticWorkflowsPermissions,
  type SiteAnalyzeAddonPermissions,
  type SiteAnnotationResourcePermissions,
  type SiteApiTokenPermissions,
  type SiteAppResourcePermissions,
  type SiteBillingResourcePermissions,
  type SiteBranchResourcePermissions,
  type AdCreativePermissions,
  type SiteCampaignPermissions,
  type SiteClientResourcePermissions,
  type SiteCollectionItemResourcePermissions,
  type SiteCollectionResourcePermissions,
  type SiteCommerceDriversResourcePermissions,
  type SiteCommerceItemResourcePermissions,
  type SiteCommerceResourcePermissions,
  type SiteCosmicPermissions,
  type SiteCustomCodeResourcePermissions,
  type SiteCustomDomainResourcePermissions,
  type SiteDesignerModesPermissions,
  type SiteFileResourcePermissions,
  type SiteFlowResourcePermissions,
  type SiteFormResourcePermissions,
  type SiteLibraryResourcePermissions,
  type SiteLinkResourcePermissions,
  type SiteLocalePermissions,
  type SiteMembershipResourcePermissions,
  type SiteMetaResourcePermissions,
  type SiteOptimizeAddonPermissions,
  type SiteOptimizeAnalyzeMetricEventPermissions,
  type SiteOptimizeAudienceEventMetricPermissions,
  type SiteOptimizeExperiencePermissions,
  type SiteOptimizeIntegrationsPermissions,
  type SiteOptimizeResultsPermissions,
  type SiteOptimizeVariationPermissions,
  type SiteOptimizeVariationStatePermissions,
  type SitePageResourcePermissions,
  type SitePageSettingsPermissions,
  type SitePermissions,
  type SiteProfileResourcePermissions,
  type SiteRedirectsResourcePermissions,
  type SiteReportResourcePermissions,
  type SiteResourcePermissions,
  type SiteRole,
  type SiteRolesForSelectType,
  type SiteRolesType,
  type SiteSearchResourcePermissions,
  type SiteSecretResourcePermissions,
  type SiteSnapshotResourcePermissions,
  type SiteStyleBlockResourcePermissions,
  type SiteSymbolResourcePermissions,
  type SiteTrackingSettingsPermissions,
  type SiteUserSystemsPermissions,
  type SiteVariableResourcePermissions,
  type SiteWebhookResourcePermissions,
} from '@packages/domains/authorization/permissions/catalog';

export const ALL_SIMULATABLE_ROLES = [
  SITE_ROLES.page_builder_v2, // Can simulate Marketer
  SITE_ROLES.editor, // Can simulate Content Editor
] as const satisfies readonly SimulatableRolesType[];

/**
 * List of site roles that can be simulated. This is used to determine which roles can be simulated
 * by the user. For example, a Project Admin can simulate a Marketer or a Content Editor.
 * Note: 'custom' is intentionally excluded - custom roles simulated based on their base role.
 */
export const SIMULATABLE_ROLES = {
  [SITE_ROLES.project_admin]: ALL_SIMULATABLE_ROLES,
  [SITE_ROLES.designer]: ALL_SIMULATABLE_ROLES,
  [SITE_ROLES.branch_designer]: [],
  [SITE_ROLES.page_builder]: [],
  [SITE_ROLES.page_builder_v2]: [],
  [SITE_ROLES.editor]: [],
  [SITE_ROLES.commenter]: [],
  [SITE_ROLES.viewer]: [],
  [SITE_ROLES.public]: [],
} as const satisfies Record<
  Exclude<SiteRolesType, 'custom'>,
  readonly SiteRolesType[]
>;

/**
 * Given a site role, returns the list of site roles that can be simulated by the user.
 *
 * Note: For custom roles, pass the base site role (e.g., 'designer', 'page_builder_v2')
 * rather than 'custom'. Use `useBaseSiteRole` hook to get the base role for custom roles.
 *
 * @param userRole - The base site role of the user to get the simulatable roles for.
 * @returns The list of simulatable roles for the user.
 */
export function getSimulatableRoles(
  userRole: SiteRolesType | null
): readonly SimulatableRolesType[] {
  if (!userRole || userRole === SITE_ROLES.custom) {
    return [];
  }
  return SIMULATABLE_ROLES[userRole];
}
